import type { QueryClient } from '@tanstack/react-query';
import produce from 'immer';
import type { getDefaultStore, WritableAtom } from 'jotai';
import type { GetTemplateCreationRecordResponse } from '../../contracts';
import {
  StreamStatus,
  type AnimationType,
  type StreamState,
  type StreamType,
} from './types';

type Store = ReturnType<typeof getDefaultStore>;

type StreamStateAtom = WritableAtom<
  StreamState,
  [StreamState | ((prev: StreamState) => StreamState)],
  void
>;

type UpdateStreamStatesBaseArgs = {
  store: Store;
  atoms: StreamStateAtom[];
};

type UpdateStreamStatesArgs =
  | (UpdateStreamStatesBaseArgs & {
      status: StreamStatus.WAITING_START;
      animationType: AnimationType;
    })
  | (UpdateStreamStatesBaseArgs & {
      status: Exclude<StreamStatus, StreamStatus.WAITING_START>;
      animationType?: never;
    });

/**
 * Creates a callback that updates stream state status for multiple atoms
 * When status is WAITING_START, animationType is required and stored in each stream state
 * When status is STREAMING, animationType is inherited from previous state
 */
export const updateStreamStates = ({
  store,
  atoms,
  status,
  animationType,
}: UpdateStreamStatesArgs) => {
  atoms.forEach((atom) => {
    store.set(atom, (prev: StreamState) => ({
      ...prev,
      status,
      animationType:
        status === StreamStatus.WAITING_START
          ? animationType
          : status === StreamStatus.STREAMING
            ? prev.animationType
            : undefined,
    }));
  });
};

/**
 * Creates a callback that conditionally updates stream state from WAITING_START to STREAMING
 */
export const createStreamStateTransitionUpdater =
  ({ store, atom }: { store: Store; atom: StreamStateAtom }) =>
  () => {
    store.set(atom, (prev: StreamState) => ({
      ...prev,
      status:
        prev.status === StreamStatus.WAITING_START ? StreamStatus.STREAMING : prev.status,
    }));
  };

/**
 * Creates standard stream event handlers with stream state management
 */
export const createStreamStateHandlers = ({
  store,
  streamStateAtom,
  onMessageCallback,
}: {
  store: Store;
  streamStateAtom: StreamStateAtom;
  onMessageCallback?: (chunk: StreamType) => void;
}) => ({
  onMessage: (chunk: StreamType) => {
    createStreamStateTransitionUpdater({ store, atom: streamStateAtom })();
    onMessageCallback?.(chunk);
  },
  onClose: () =>
    updateStreamStates({
      store,
      atoms: [streamStateAtom],
      status: StreamStatus.IDLE,
    }),
  onError: (error: unknown) => {
    updateStreamStates({
      store,
      atoms: [streamStateAtom],
      status: StreamStatus.ERROR,
    });
    throw error;
  },
});

/**
 * Updates version control field in query data
 */
export const updateVersionControlInQueryData = <
  TField extends keyof NonNullable<
    GetTemplateCreationRecordResponse['version_controls'][number]
  >,
>({
  queryClient,
  queryKey,
  versionControlId,
  field,
  updater,
}: {
  queryClient: QueryClient;
  queryKey: unknown[];
  versionControlId: string;
  field: TField;
  updater: (
    currentValue: NonNullable<
      GetTemplateCreationRecordResponse['version_controls'][number]
    >[TField]
  ) => NonNullable<GetTemplateCreationRecordResponse['version_controls'][number]>[TField];
}) => {
  queryClient.setQueryData<GetTemplateCreationRecordResponse>(queryKey, (oldData) => {
    if (!oldData) {
      return undefined;
    }

    return produce(oldData, (draft) => {
      const versionControl = draft.version_controls.find(
        (vc) => vc.id === versionControlId
      );
      if (versionControl) {
        versionControl[field] = updater(versionControl[field]);
      }
    });
  });
};

/**
 * Appends chunk data to a version control field in query data
 */
export const appendToVersionControlField = ({
  queryClient,
  queryKey,
  versionControlId,
  field,
  chunk,
}: {
  queryClient: QueryClient;
  queryKey: unknown[];
  versionControlId: string;
  field: 'template_structure' | 'example_note';
  chunk: StreamType;
}) => {
  queryClient.setQueryData<GetTemplateCreationRecordResponse>(queryKey, (oldData) => {
    if (!oldData) {
      return undefined;
    }

    return produce(oldData, (draft) => {
      const versionControl = draft.version_controls.find(
        (vc) => vc.id === versionControlId
      );
      if (versionControl) {
        versionControl[field] += chunk.data;
      }
    });
  });
};
